import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";

const navItems = [
  { label: "Startseite", href: "/" },
  { label: "Sträuße", href: "/strauss-geschenk" },
  { label: "Hochzeit", href: "/hochzeit" },
  { label: "Trauer", href: "/trauer" },
  { label: "Event", href: "/event" },
  { label: "Pflanzen", href: "/pflanzen" },
  { label: "Über uns", href: "/ueber-uns" },
  { label: "Kontakt", href: "/kontakt" },
];

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const isHome = location.pathname === "/";

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled || !isHome
          ? "bg-background-50/90 backdrop-blur-sm border-b border-background-200/70 lg:bg-background-50/95 lg:backdrop-blur-md"
          : "bg-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="flex items-center justify-between h-18 md:h-20">
          {/* Logo – breit, nach Breite skaliert für bessere Lesbarkeit */}
          <Link
            to="/"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="flex items-center shrink-0 py-2"
          >
            <img
              src="https://storage.helloreaddy.io/project_files/ccd3f37f-a1a3-49ba-8460-6107752c615a/e2ddba3c-4c3c-4dd5-8a73-9e37712e7c2a_compressed_BlumenSthr_refresh_trans_001b.webp"
              alt="Florale Manufaktur Blumen Stöhr – Logo"
              className="h-auto w-auto max-h-12 md:max-h-14 max-w-[200px] md:max-w-[260px] lg:max-w-[300px] object-contain"
            />
          </Link>

          {/* Desktop Navigation – kompaktere Labels */}
          <nav className="hidden lg:flex items-center gap-0.5">
            {navItems.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className={`px-2.5 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
                  location.pathname === item.href
                    ? "text-primary-700 bg-primary-50"
                    : "text-foreground-700 hover:text-primary-600 hover:bg-primary-50/50"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center gap-3 shrink-0">
            <a
              href="tel:+494914238"
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-accent-500 text-background-50 rounded-md text-base font-medium hover:bg-accent-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-phone-line w-4 h-4 flex items-center justify-center"></i>
              0491 4238
            </a>
          </div>

          {/* Mobile Hamburger */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 text-foreground-700 hover:text-primary-600 transition-colors"
            aria-label={isMobileMenuOpen ? "Menü schließen" : "Menü öffnen"}
            aria-expanded={isMobileMenuOpen}
          >
            <i className={`ri-${isMobileMenuOpen ? "close" : "menu"}-line w-6 h-6 flex items-center justify-center text-xl`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden absolute top-full left-0 right-0 overflow-hidden transition-all duration-300 ${
          isMobileMenuOpen ? "max-h-[80vh] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <nav className="px-4 py-4 space-y-1 bg-background-50/90 backdrop-blur-sm border-b border-background-200/70 shadow-lg">
          {navItems.map((item) => (
            <Link
              key={item.href}
              to={item.href}
              className={`block px-4 py-3 text-sm font-medium rounded-md transition-colors ${
                location.pathname === item.href
                  ? "text-primary-700 bg-primary-50"
                  : "text-foreground-800 hover:text-primary-600 hover:bg-primary-50/50"
              }`}
            >
              {item.label}
            </Link>
          ))}
          <div className="pt-3 border-t border-background-200/70 mt-3">
            <a
              href="https://www.fleurop.de/partnerfloristen/fleurop-filialen/26789-leer/florale-manufaktur-blumen-stoehr-gmbh-heisfelder-str-105"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 px-4 py-3 bg-accent-500 text-background-50 rounded-md text-sm font-medium hover:bg-accent-600 transition-colors"
            >
              <i className="ri-send-plane-line w-4 h-4 flex items-center justify-center"></i>
              Blumen online versenden
            </a>
          </div>
        </nav>
      </div>
    </header>
  );
}